import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useReceptionReasonOptions } from "@/features/realEstateReceptionBookFeed/hooks/useReceptionReasonOptions";
import {
  getSearchMaxDate,
  getSearchMinDate,
  getStartAndEndDate,
} from "@/features/realEstateReceptionBookFeed/utils/dateTime";
import { type ParseResult } from "papaparse";
import { useFeatureFlags } from "@/configs/featureFlag";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useApiClient } from "@/hooks/useApiClient";
import { HttpStatusCode, isAxiosError } from "axios";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { Box, Grid, Link, Typography, Stack, Divider } from "@mui/material";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { PageTitle } from "@/components/Title";
import { FileUploadButton } from "@/features/realEstateReceptionBookFeed/components/FileUploadButton";
import { CMultiSelectWithSpecialOptions } from "@/features/monitoring/components/CMultiSelectWithSpecialOptions";
import { CDateRangePicker } from "@/components/DatePicker/CDateRangePicker";
import { Button, ButtonVariantOption, SubmitButton } from "@/components/Button";
import { KeyboardArrowLeft } from "@mui/icons-material";
import SavedSearchIcon from "@mui/icons-material/SavedSearch";
import { CTextFieldIdentifyName } from "@/components/TextField/CTextFieldIdentifyName";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { type PostMonitoringFileAPIResponse } from "../types";
import { PostMonitoringFromFileAPI } from "../api";
import { CustomModal } from "@/components/Modal";
import { CCheckbox } from "@/components/Checkbox/CCheckbox";
import { SendToTrackingService } from "@/api/handlers/errors";
import { Label } from "@/components/Label";
import { UserConfigGetAPI } from "@/features/accountSettings/api/UserConfigAPI";
import { useMonitoringAmount } from "../hooks/useMonitoringAmount";
import { calculateTermMonth } from "../utils/calculateTermMonth";
import { calculateLimitMonth } from "../utils/calculateLimitMonth";
import { readFile } from "@/utils/readFile";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";

const ONCE_UPDATE_MAX_CSV_ROWS = 1_000;
const MONTHLY_UPDATE_MAX_CSV_ROWS = 5_000;

/**
 * 受けとったCSVファイルの要素の件数が指定された件数を超えているか判定する
 * @param content papaparseのCSVパース結果オブジェクト
 * @param count 最大値
 * @return 最大値を超えているならtrue、それ以外はfalse
 */
const isLineCountExceeded = (
  content: ParseResult<unknown>,
  count: number
): boolean => {
  return content.data.length > count;
};

/**
 * CSVファイルから重複行を取り除く
 * 重複取り除きルールは「不動産種別、都道府県、所在、地番または家屋番号」が全て一致している行
 * 重複があった場合、最初の要素のみ残し、後の要素は削除する
 * @param content papaparseのCSVパース結果オブジェクト
 * @return contentから重複行を取り除いたオブジェクト
 */
const distinctCSV = (content: ParseResult<unknown>): ParseResult<unknown> => {
  const newData: Array<Record<string, string>> = [];

  const getKey = (input: Record<string, string>): string => {
    return (
      input["不動産種別"] +
      input["都道府県"] +
      input["所在"] +
      input["地番または家屋番号"]
    );
  };

  content.data.forEach((value) => {
    const key = getKey(value as Record<string, string>);
    if (newData.some((value) => getKey(value) === key)) {
      return;
    }
    newData.push(value as Record<string, string>);
  });
  content.data = newData;
  return content;
};

export const MonitoringUpload: React.FC = () => {
  const { realEstateRegistrationMonitoring } = useFeatureFlags();

  // ユーザコンフィグを取得するフック
  const userConfig = UserConfigGetAPI();

  // アップロード対象のファイルオブジェクト
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [rowCount, setRowCount] = useState<number>(0);

  // MAX_CSV_ROWS件越えエラーメッセージ関連のState
  const [showFileErrorMessage, setShowFileErrorMessage] =
    useState<boolean>(false);

  // 料金がかかる旨を通知するためのState
  const [fileNoticeMessage, setFileNoticeMessage] = useState<string[]>([]);

  // 実行ボタン無効化用State
  const [executeButtonDisabled, setExecuteButtonDisabled] =
    useState<boolean>(true);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  // 確認ボタン押下後のキャンセルボタン無効化State
  const [isPushedConfirmButton, setIsPushedConfirmButton] =
    useState<boolean>(false);

  // 登記原因のState
  const [receptionReasons, setReceptionReasons] = useState<string[]>([]);

  // 登記要因セレクト項目
  const { receptionReasonOptions } = useReceptionReasonOptions({ short: true });

  // モニタリングに関する金額関連
  const { data: amount } = useMonitoringAmount();

  // ルーティング用
  const navigate = useNavigate();

  // 法務局受付日 開始、終了の初期値
  const [startDate, endDate] = getStartAndEndDate();
  const minDate = getSearchMinDate();
  const maxDate = getSearchMaxDate();

  // React-Hook-Form用スキーマ定義
  const UploadFormDataSchema = z.object({
    identifyName: z
      .string()
      .trim()
      .min(1, { message: "識別名を入力してください" })
      .max(256, { message: "256文字まで入力可能です" }),
    legalAffairsBureauRequestDateRange: z.tuple([
      z.date().min(minDate).max(maxDate),
      z.date().min(minDate).max(maxDate),
    ]),
    notMonthlyUpdatable: z.boolean(),
    receptionReasons: z
      .array(z.string())
      .min(1, "１つ以上登記原因を選択してください"),
  });

  // 定義したZodのスキーマをTypescriptの型に変換
  type UploadFormData = z.infer<typeof UploadFormDataSchema>;

  const reactHookForm = useForm<UploadFormData>({
    mode: "all",
    defaultValues: {
      identifyName: "",
      legalAffairsBureauRequestDateRange: [startDate, endDate],
      notMonthlyUpdatable: false,
      receptionReasons: [],
    },
    resolver: zodResolver(UploadFormDataSchema),
  });

  const [watchNotMonthlyUpdatable, watchLegalAffairsBureauRequestDateRange] =
    reactHookForm.watch([
      "notMonthlyUpdatable",
      "legalAffairsBureauRequestDateRange",
    ]);

  const { apiClient } = useApiClient();
  const isReadOnly = useReadOnlyUser();

  useEffect(() => {
    const legalAffairsBureauRequestDateStart = reactHookForm.getValues(
      "legalAffairsBureauRequestDateRange"
    )[0];
    const legalAffairsBureauRequestDateEnd = reactHookForm.getValues(
      "legalAffairsBureauRequestDateRange"
    )[1];

    if (
      !legalAffairsBureauRequestDateStart ||
      !legalAffairsBureauRequestDateEnd ||
      Number.isNaN(legalAffairsBureauRequestDateStart.getTime()) ||
      Number.isNaN(legalAffairsBureauRequestDateEnd.getTime()) ||
      legalAffairsBureauRequestDateStart > legalAffairsBureauRequestDateEnd
    ) {
      setFileNoticeMessage([]);
      setExecuteButtonDisabled(true);
      return;
    }

    if (!amount || amount.price <= 0) return;
    let termMonth = calculateTermMonth(
      legalAffairsBureauRequestDateStart,
      legalAffairsBureauRequestDateEnd
    );
    const message = [];
    const unitPrice = amount?.price; // 1件あたりのモニタリング金額
    const maximumPrice = amount?.maximum; // 1件あたりのモニタリング課金額上限
    if (rowCount) {
      if (reactHookForm.getValues("notMonthlyUpdatable")) {
        const limitMonth = calculateLimitMonth(unitPrice, maximumPrice);
        if (termMonth > limitMonth) {
          termMonth = limitMonth;
        }
        const totalCost = (rowCount * termMonth * unitPrice).toLocaleString(
          "ja-JP"
        );
        message.push(`注意: 実行すると${totalCost}円の費用が発生します。`);
        message.push(
          `過去分1物件あたりの上限金額は${maximumPrice.toLocaleString(
            "ja-JP"
          )}円です。`
        );
      } else {
        const monthlyCost = (rowCount * unitPrice).toLocaleString("ja-JP");
        message.push(
          `注意: 実行すると毎月最大${monthlyCost}円の費用が発生します。`
        );
      }
    }
    setFileNoticeMessage(message);

    const isRowCountError = reactHookForm.getValues("notMonthlyUpdatable")
      ? rowCount > ONCE_UPDATE_MAX_CSV_ROWS
      : rowCount > MONTHLY_UPDATE_MAX_CSV_ROWS;

    setShowFileErrorMessage(isRowCountError);
    setExecuteButtonDisabled(isRowCountError);
  }, [
    rowCount,
    watchNotMonthlyUpdatable,
    watchLegalAffairsBureauRequestDateRange,
    amount,
  ]);

  // モニタリング機能無効の場合は何も表示しない
  if (!realEstateRegistrationMonitoring) return null;

  /**
   * ファイル選択時の処理
   * - エラーメッセージ削除
   * - CSVファイルまたはExcelファイルの読み込み
   * - ファイルMAX_CSV_ROWS件制限の確認
   * - ファイル重複項目の削除
   * - エラーの場合はメッセージ表示
   * - 成功の場合は実行ボタンの有効化
   * @param event
   * @return void
   */
  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    setShowFileErrorMessage(false);
    setExecuteButtonDisabled(true);
    setFileToUpload(null);

    // ファイルが選択されているときのみ確認画面へボタンを有効にする
    const files = event.target.files ?? [];
    if (files.length > 0) {
      const file = files[0];
      try {
        const fileData = await readFile(file);
        setRowCount(fileData.data.length);

        if (reactHookForm.getValues("notMonthlyUpdatable")) {
          if (isLineCountExceeded(fileData, ONCE_UPDATE_MAX_CSV_ROWS)) {
            setShowFileErrorMessage(true);
            setFileNoticeMessage([]);
            return;
          }
        } else {
          if (isLineCountExceeded(fileData, MONTHLY_UPDATE_MAX_CSV_ROWS)) {
            setShowFileErrorMessage(true);
            setFileNoticeMessage([]);
            return;
          }
        }

        const resultData = distinctCSV(fileData);
        setRowCount(resultData.data.length);
        setExecuteButtonDisabled(false);
        setFileToUpload(file);
      } catch (error) {
        // ファイル読み込みエラーの場合
        toast.error(
          error instanceof Error
            ? error.message
            : "ファイルの読み込みに失敗しました"
        );
        setExecuteButtonDisabled(true);
        setFileToUpload(null);
        setRowCount(0);
        setFileNoticeMessage([]);
      }
    } else {
      // ファイルが選択されていない場合
      setExecuteButtonDisabled(true);
      setFileToUpload(null);
      setRowCount(0);
      setFileNoticeMessage([]);
    }
  };

  // CSVアップロードの確認ボタンが押された時の処理
  const handlePushedConfirmButton = (): void => {
    setIsOpen(true);
  };

  // 確認モーダルで「はい」を選択した時の処理
  const handleCsvUploadButtonClick = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    cancelLoading: () => void
  ): void => {
    if (!fileToUpload || !reactHookForm.formState.isValid) {
      return;
    }

    // NOTE: 月初日を取得するために、月を1進めて1日目を指定する
    const legalAffairsBureauRequestDateStart = new Date();
    legalAffairsBureauRequestDateStart.setFullYear(
      reactHookForm
        .getValues("legalAffairsBureauRequestDateRange")[0]
        .getFullYear(),
      reactHookForm
        .getValues("legalAffairsBureauRequestDateRange")[0]
        .getMonth(),
      1
    );

    // NOTE: 月末日を取得するために、月を1進めて0日目を指定する
    const legalAffairsBureauRequestDateEnd = new Date();
    legalAffairsBureauRequestDateEnd.setFullYear(
      reactHookForm
        .getValues("legalAffairsBureauRequestDateRange")[1]
        .getFullYear(),
      reactHookForm
        .getValues("legalAffairsBureauRequestDateRange")[1]
        .getMonth() + 1,
      0
    );

    setIsPushedConfirmButton(true);
    PostMonitoringFromFileAPI(apiClient, {
      file: fileToUpload,
      name: reactHookForm.getValues("identifyName"),
      receptionReasons: reactHookForm.getValues("receptionReasons"),
      monthlyUpdatable: !reactHookForm.getValues("notMonthlyUpdatable"),
      legalAffairsBureauRequestDateStart,
      legalAffairsBureauRequestDateEnd,
    })
      .then((response) => {
        navigate(`/monitoring/${response.id ?? ""}`);
      })
      .catch((error) => {
        if (
          isAxiosError<PostMonitoringFileAPIResponse>(error) &&
          error.response?.status === HttpStatusCode.BadRequest
        ) {
          toast.warning(
            <>
              ファイルのアップロードに失敗しました。
              <br />
              ファイル内容をご確認ください。
            </>
          );
          return;
        }
        if (
          isAxiosError<PostMonitoringFileAPIResponse>(error) &&
          (error.response?.status ?? HttpStatusCode.InternalServerError) >=
            HttpStatusCode.InternalServerError
        ) {
          SendToTrackingService(error);
          toast.error("ファイルのアップロードに失敗しました");
        }
      })
      .finally(() => {
        cancelLoading();
        setIsPushedConfirmButton(false);
      });
  };

  return (
    <PagePaper>
      <form>
        {/* ページタイトル */}
        <Box sx={{ display: "flex", mb: 0 }}>
          <SavedSearchIcon sx={{ mr: 1 }} fontSize="large" />
          <PageTitle>不動産データアップロード</PageTitle>
        </Box>
        <Stack spacing={1} sx={{ ml: 5, mb: 3, width: "fit-content" }}>
          <Link href={"/assets/sample_R.E.DATAモニタリングサンプル.csv"}>
            見本CSVをダウンロード
          </Link>
          <Link href={"/assets/sample_R.E.DATAモニタリングサンプル.xlsx"}>
            見本Excelをダウンロード
          </Link>
        </Stack>

        <SectionPaper>
          <Box sx={{ m: 3 }}>
            <Grid container justifyContent="center">
              <Grid item xs={6}>
                <Grid container direction="column" spacing={4}>
                  <Grid item>
                    <Label required={true}>識別名</Label>
                    <CTextFieldIdentifyName
                      name={"identifyName"}
                      control={reactHookForm.control}
                      size={SizingWrapperStyle.LARGE}
                    />
                  </Grid>
                  <Grid item>
                    <Label required={true}>登記原因</Label>
                    <CMultiSelectWithSpecialOptions<UploadFormData>
                      name={"receptionReasons"}
                      control={reactHookForm.control}
                      label="登記原因"
                      options={receptionReasonOptions}
                      value={receptionReasons}
                      onChange={setReceptionReasons}
                    />
                  </Grid>
                  <Grid item>
                    <CCheckbox<UploadFormData>
                      name={"notMonthlyUpdatable"}
                      control={reactHookForm.control}
                      label={"月次でモニタリングしない"}
                    />
                  </Grid>
                  <Grid item>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                      }}
                    >
                      <CDateRangePicker<UploadFormData>
                        name={"legalAffairsBureauRequestDateRange"}
                        control={reactHookForm.control}
                        startDateLabel="法務局受付日 開始"
                        endDateLabel="法務局受付日 終了"
                        minDate={minDate}
                        maxDate={maxDate}
                        defaultCalendarMonth={startDate}
                        disabled={!watchNotMonthlyUpdatable}
                        views={["year", "month"]}
                        inputFormat={"yyyy/MM"}
                        mask={"____/__"}
                        displayDateLabel={true}
                        requiredStart={watchNotMonthlyUpdatable}
                        requiredEnd={watchNotMonthlyUpdatable}
                      />
                    </Box>
                  </Grid>

                  <Grid item>
                    <Label required={true}>
                      CSVまたはエクセルをアップロードする
                    </Label>
                    <Box display={"flex"} justifyContent="start" my={2} gap={3}>
                      <FileUploadButton
                        variant={ButtonVariantOption.Outlined}
                        onChange={(event) => {
                          (async (): Promise<void> => {
                            await handleFileSelected(event);
                          })();
                        }}
                        accept={".csv, .xlsx, .xls"}
                        label={"アップロード"}
                      />
                      <Typography variant="body1" sx={{ lineHeight: 2 }}>
                        {fileToUpload
                          ? fileToUpload?.name
                          : "選択されていません"}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ lineHeight: 2 }}>
                      アップロードボタンを押してファイルを選択してください
                    </Typography>
                  </Grid>

                  {showFileErrorMessage && (
                    <Grid item>
                      <Typography textAlign={"left"} color={"error"}>
                        {`ファイルの項目数は${(watchNotMonthlyUpdatable
                          ? ONCE_UPDATE_MAX_CSV_ROWS
                          : MONTHLY_UPDATE_MAX_CSV_ROWS
                        ).toLocaleString(
                          "ja-JP"
                        )}以下にしてください。現在の項目数: ${rowCount.toLocaleString(
                          "ja-JP"
                        )}件`}
                      </Typography>
                    </Grid>
                  )}

                  {fileNoticeMessage.length > 0 &&
                    !showFileErrorMessage &&
                    userConfig !== undefined &&
                    !userConfig.isPriceHidden && (
                      <Grid item>
                        <Typography textAlign={"left"} color={"error"}>
                          {fileNoticeMessage.map((message, idx) => (
                            <div key={idx}>{message}</div>
                          ))}
                          重複行を考慮しない概算金額です。重複排除はアップロード後に行われます。
                        </Typography>
                      </Grid>
                    )}
                </Grid>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Grid item mb={2}>
            <Box display="flex" justifyContent="center" alignItems="center">
              <ReadOnlyTooltip isReadOnly={isReadOnly}>
                <Button
                  label={"実行する"}
                  disabled={
                    isReadOnly ||
                    !reactHookForm.formState.isValid ||
                    executeButtonDisabled ||
                    !fileToUpload
                  }
                  onClick={handlePushedConfirmButton}
                  variant={ButtonVariantOption.Contained}
                />
              </ReadOnlyTooltip>
            </Box>
          </Grid>
        </SectionPaper>

        {/* 戻る */}
        <Box sx={{ mt: 2 }}>
          <Link
            component={RouterLink}
            to={"/monitoring"}
            sx={{ display: "inline-block" }}
          >
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>不動産登記モニタリング画面に戻る</Typography>
            </Box>
          </Link>
        </Box>
      </form>
      {/* 課金確認モーダル */}
      <CustomModal
        isOpen={isOpen}
        handleClose={(
          event: Record<string, unknown>,
          reason: "backdropClick" | "escapeKeyDown"
        ): void => {
          if (
            reason &&
            (reason === "backdropClick" || reason === "escapeKeyDown")
          ) {
            return;
          }
          setIsOpen(false);
        }}
        ariaLabelledby="confirm-modal"
        ariaDescribedby="confirm-modal-description"
      >
        <PageTitle>確認</PageTitle>
        <Box>
          <Typography>課金が発生します。よろしいですか？</Typography>

          <Box
            sx={{
              mr: 2,
              my: 3,
              display: "flex",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Button
              label="いいえ"
              disabled={isPushedConfirmButton}
              onClick={() => {
                setIsOpen(false);
              }}
              variant={ButtonVariantOption.Outlined}
            />
            <ReadOnlyTooltip isReadOnly={isReadOnly}>
              <SubmitButton
                label={"はい"}
                timeout={120_000} // ms
                onClick={handleCsvUploadButtonClick}
                variant={ButtonVariantOption.Contained}
                disabled={isReadOnly}
              />
            </ReadOnlyTooltip>
          </Box>
        </Box>
      </CustomModal>
    </PagePaper>
  );
};
